import { ref, watch } from 'vue';
import { useScroll } from '@vueuse/core';

/**
 * Tracks whether the site header should render in its minified variant.
 *
 * Scrolling down past the threshold minifies the header, scrolling back up
 * restores the full size. Movements below `delta` are ignored so that the
 * header does not flicker on trackpad jitter.
 *
 * Reading the direction from the scroll position only holds as long as the
 * header cannot move the page itself: a header that takes part in the flow
 * shifts everything below it by the height a morph adds or removes, the
 * browser keeps that content visually still by moving the scroll position
 * along, and the next movement this composable measures is then its own
 * correction, pointing against the movement that caused the morph. The header
 * flips back, moves the page again, and keeps oscillating for as long as the
 * page is scrolled. `SiteHeader` therefore keeps the header out of the flow.
 *
 * @param {object} [options]
 * @param {import('vue').Ref<number>} [options.y] Scroll position, defaults to the window scroll.
 * @param {number} [options.threshold] Scroll offset below which the header always stays full size.
 * @param {number} [options.delta] Minimum scroll movement that may change the state.
 * @returns {{ minified: import('vue').Ref<boolean> }}
 */
export function useHeaderMorph(options = {}) {
    const {
        y = useScroll(globalThis.window).y,
        threshold = 112,
        delta = 8,
    } = options;

    const minified = ref(false);
    let previousY = y.value;

    watch(y, currentY => {
        if (currentY <= threshold) {
            minified.value = false;
            previousY = currentY;

            return;
        }

        const movement = currentY - previousY;

        if (Math.abs(movement) < delta) {
            return;
        }

        minified.value = movement > 0;
        previousY = currentY;
    });

    return { minified };
}
